import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { track } from "@/features/analytics";
import { useUpdateMediaFolderMutation } from "@api/media-folders";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { type KeyboardEventHandler, useState } from "react";
import { toast } from "react-toastify";
import { MediaLibDialogBase } from "../../media-lib-dialog-base";

const DIALOG_ID = "media-lib-folder-rename";

interface IProps {
  folderId: string;
  folderName: string;
}

export const MediaLibFolderRenameDialog = ({
  folderId,
  folderName,
}: IProps) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState(folderName);

  const [updateFolder, { isLoading }] = useUpdateMediaFolderMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (!name?.trim() || isLoading) return;

    try {
      await updateFolder({
        id: folderId,
        title: name.trim(),
      }).unwrap();
    } catch (e) {
      track("error updating media folder name", {
        folderId,
        folderName: name.trim(),
        error: e,
      });
      toast.error("Error updating folder name");
    }

    handleClose();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header title="Rename folder" onClose={handleClose} />
      <MediaLibDialogBase.Content>
        <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
          Name
        </span>
        <Input
          placeholder="Add your folder name here..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!name?.trim()}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibFolderRenameDialog.dialogId = DIALOG_ID;
